import { useState, useEffect, useRef } from 'react';
import '../styles/pages/Product.scss'
import LoadingComponent from '../components/LoadingComponent';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { capitalizeFirstLetter, SERVER } from '../hooks/config';
import prod1 from '../assets/products/prod1.png';
import prod3 from '../assets/products/prod3.png';
import prod4 from '../assets/products/prod4.png';
import { MdOutlineChevronRight } from "react-icons/md";
import ProductCard from '../components/ProductCard';
import { useCart } from '../contexts/CartContext';
import Modal from '../components/Modal';

const Product = () => {
    const { addToCart } = useCart();
    const location = useLocation();
    const navigate = useNavigate();
    const { name } = useParams(); // Get name from URL
    const { id: stateId } = location.state || {}; // Fallback to state if available
    const [quantity, setQuantity] = useState(1);
    const [product, setProduct] = useState(null);
    const [otherProducts, setOtherProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [display, setDisplay] = useState('description');
    const [currentImage, setCurrentImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedSize, setSelectedSize] = useState(null);
    const guideRef = useRef(null); 

    const handleShowGuide = () => {
        setDisplay('guide');
        setTimeout(() => {
            if (guideRef.current) {
            const elementTop = guideRef.current.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementTop - 100;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            }
        }, 150);
    };


    const handleAddToCart = () => {
        const productWithSize = {
            ...product,
            selectedSize: selectedSize
        };
        addToCart(productWithSize, quantity);
        setShowModal(true);
    };
    
    useEffect(() => {
        if (name) {
            fetchProduct();
        }
    }, [name]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            // Fetch product by name from URL
            const response = await axios.get(`${SERVER}/products/by-name/${name}`);
            const productData = response.data;
            setProduct(productData);
            setCurrentImage(productData.banner ? `${SERVER}${productData.banner}` : prod1);
            
            // Fetch related products using the product ID
            if (productData.id) {
                fetchRelatedProducts(productData.id);
            }
        } catch (error) {
            console.error('Error fetching product:', error);
            // Fallback to dummy product if API fails
            setProduct({
                id: null,
                name: name || 'Produit non trouvé',
                code: 'N/A',
                fullname: 'Produit non disponible',
                general_description: 'Ce produit n\'est pas disponible pour le moment.',
                description: 'Aucune description disponible.',
                materiel: 'N/A',
                finition: 'N/A',
                dimension: 'N/A',
                ideal: 'N/A',
                banner: prod1,
                images: [prod1],
                price: 0,
                promotion: 0,
                tailles: [], // Empty sizes array for fallback
                guide_taille: null
            });
            setCurrentImage(prod1);
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedProducts = async (productId) => {
        try {
            const response = await axios.get(`${SERVER}/products/${productId}/related?limit=4`);
            setOtherProducts(response.data);
        } catch (error) {
            console.error('Error fetching related products:', error);
            // Fallback to dummy related products if API fails
            setOtherProducts([
                { id: 2, banner: prod1, name: 'Boucles d\'oreille', price: '70', promotion: '' },
                { id: 3, banner: prod4, name: 'Collier motif floral', price: '70', promotion: '' },
                { id: 4, banner: prod3, name: 'Bracelet Bloom motif en argent', price: '30', promotion: '' },
                { id: 5, banner: prod1, name: 'Produit 5', price: '50', promotion: '' },
            ]);
        }
    };

    const handleImageClick = (clickedImage) => {
        if (!product) return;
        
        // If the clicked image is already the current banner, do nothing
        if (clickedImage === product.banner) return;
        
        // Check if the clicked image is in the images array
        const isInImages = product.images.includes(clickedImage);
        
        if (isInImages) {
            // Swap the clicked image with the banner
            setProduct(prevProduct => {
                const newImages = [...prevProduct.images];
                
                // Remove the clicked image from the images array
                const clickedImageIndex = newImages.indexOf(clickedImage);
                if (clickedImageIndex !== -1) {
                    newImages.splice(clickedImageIndex, 1);
                }
                
                // Add the current banner to the images array (if it exists)
                if (prevProduct.banner) {
                    newImages.push(prevProduct.banner);
                }
                
                // Return the updated product with the clicked image as the new banner
                return {
                    ...prevProduct,
                    banner: clickedImage,
                    images: newImages
                };
            });
            
            // Update the current image view
            setCurrentImage(clickedImage.startsWith('/uploads/') ? `${SERVER}${clickedImage}` : clickedImage);
        }
    };

    if (loading) {
        return <LoadingComponent />;
    }

    if (!product) {
        return <div>Produit non trouvé</div>;
    }

    // Get only the first 3 images for the right side
    const displayImages = product.images ? product.images.slice(0, 3) : [];

    return(
        <div className="product-page">
            <div className='breadcramp'>
                <button onClick={() => navigate('/Products')}> Tous les produits </button>
                <MdOutlineChevronRight />
                <span> {product.name} </span>
            </div>
            <div className='product-container'>
                <div className='product-header'>
                    <div className='product-images'>
                        <div className='main-image'>
                            <img src={currentImage} alt={product.name} />
                            {product.promotion > 0 ? (
                                <div className='promotion-percentage'>
                                    {Math.round(product.promotion)}%
                                </div>
                            ) : product.new_product === true && (
                                <div className='new-product'>
                                    NEW
                                </div>
                            )}
                        </div>
                        <div className='other-images'>
                            {/* Show only the first 3 images */}
                            {displayImages.map((image, index) => (
                                <img 
                                    key={index}
                                    src={image.startsWith('/uploads/') ? `${SERVER}${image}` : image} 
                                    alt={`${product.name} ${index + 1}`} 
                                    onClick={() => handleImageClick(image)}  
                                />
                            ))}
                        </div>
                    </div>
                    <div className='product-details'>
                        <h1>
                        {product.fullname
                            ? capitalizeFirstLetter(product.fullname)
                            : capitalizeFirstLetter(product.name)}
                        </h1>
                        <span className='product-code'> {product.code} </span>
                        {product.promotion > 0 ? (
                            <div className='price-after-promotion'>
                                <span className='old-price'> {product.price} DT </span>
                                <span className='actual-price'> {product.price - (product.price * product.promotion / 100)} DT </span>
                            </div>
                        ) : (
                            <span className='product-price'> {product.price} DT </span>
                        )}
                        <p className='product-general-desc'> {product.general_description} </p>
                        {product.tailles && product.tailles.length > 0 && (
                            <div className='size-selection'>
                                <span className='size-label'>Taille : { product.guide_taille && <a onClick={handleShowGuide}>Voir guide des tailles</a>}</span>
                                <div className='size-options'>
                                    {product.tailles.map((size, index) => (
                                        <button
                                            key={index}
                                            className={`size-option ${selectedSize === size ? 'selected' : ''}`}
                                            onClick={() => setSelectedSize(size)}
                                        >
                                            {capitalizeFirstLetter(size)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className='quantity-container'>
                            <span> Quantité </span>
                            <div className='quantity-control'>
                                <button disabled={quantity === 1} onClick={() => {if (quantity !== 1) setQuantity(quantity-1)}}> - </button>
                                <span> {quantity} </span>
                                <button onClick={() => setQuantity(quantity+1)}> + </button>
                            </div>
                        </div>
                        <div className='product-utils'>
                            <button 
                                className='add-to-cart' 
                                onClick={handleAddToCart}
                                disabled={product.tailles && product.tailles.length > 0 && !selectedSize}
                            >
                                Ajouter au panier
                            </button>
                            <button onClick={() => navigate('/ArticlesPersonnalise', { state: { id : product.id } })}> Personaliser ce produit </button>
                        </div>
                        <button className='btn-contact-cons' onClick={() => navigate('/Contact')}> Contacter un conseiller </button>
                    </div>
                </div>
                <div className='product-description-container'>
                    <div className='product-buttons-display'>
                        <button className={display === 'description' ? 'clicked' : ''} onClick={() => setDisplay('description')}> Description </button>
                        <button className={display === 'chars' ? 'clicked' : ''} onClick={() => setDisplay('chars')}> Caractéristiques </button>
                        {product.guide_taille && (
                            <button className={display === 'guide' ? 'clicked' : ''} onClick={() => setDisplay('guide')}> Guide Tailles </button>
                        )}
                    </div>
                    {display === 'description' ? (
                       <div className='product-description'>
                       {product?.description?.split('\n').map((line, index) => (
                         <span key={index}>
                           {line}
                           <br />
                         </span>
                       ))}
                     </div>                         
                    ) : display === 'chars' ? (
                        <div className='product-chars'>
                            <div className='chars-grid'>
                                {product.materiel && (
                                    <div className='char-item'>
                                        <span className='char-label'>Matériau:</span>
                                        <span className='char-value'>{product.materiel}</span>
                                    </div>
                                )}
                                {product.finition && (
                                    <div className='char-item'>
                                        <span className='char-label'>Finition:</span>
                                        <span className='char-value'>{product.finition}</span>
                                    </div>
                                )}
                                {product.dimension && (
                                    <div className='char-item'>
                                        <span className='char-label'>Dimensions:</span>
                                        <span className='char-value'>{product.dimension}</span>
                                    </div>
                                )}
                                {product.ideal && (
                                    <div className='char-item'>
                                        <span className='char-label'>Idéal pour:</span>
                                        <span className='char-value'>{product.ideal}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className='guide-tailles-container' ref={guideRef}>
                            <img src={SERVER+product.guide_taille} alt={product.name + ' Guide tailles'} />
                        </div>
                    )}
                </div>
                <div className='other-products'>
                    {otherProducts.length > 0 && (
                        <>
                            <h3>Produits similaires</h3>
                            <div className='other-products-grid'>
                                {otherProducts.map((otherProduct) => (
                                    <ProductCard
                                        key={otherProduct.id}
                                        product={otherProduct}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Success Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                type="success"
                title="Produit ajouté au panier!"
                message={`${product.name} ${selectedSize ? `(Taille: ${selectedSize})` : ''} a été ajouté à votre panier.`}
                autoClose={true}
                autoCloseTime={3000}
                showCloseButton={true}
            />
        </div>
    );
};

export default Product;